import { useTranslation } from "react-i18next";

import SearchableSelect from "./SearchableSelect";

/**
 * The "Branch Manager" block on the branch form: either pick a user who already
 * works here, or create an account for a new one. `value` is
 * { managerMode, userId, newManager: { name, email, password, passwordConfirmation } }.
 *
 * `errors` is the API's 422 map, read at the same keys the API sends
 * (`user_id`, `new_manager.email`, …) so both error sources land on one input.
 *
 * `usersFailed` means the candidate list could not be loaded — a different
 * thing from the business having nobody, and OUP-26 is what happens when the
 * two are conflated: a 403 on /business/users used to render as "this business
 * has no other users yet".
 *
 * `modes` is which choices to offer. Creating a branch offers the two real
 * ones; editing one adds "unchanged" first, so an edit is not forced to
 * re-answer a question it is not asking. `currentManager` names who runs the
 * branch today, which is the whole point of offering that choice.
 */
export default function BranchManagerFields({
  value,
  onChange,
  users = [],
  errors = {},
  loading,
  modes = ["existing", "new"],
  currentManager = null,
  usersFailed = false,
}) {
  const { t } = useTranslation();

  const fieldError = (name) => errors[name]?.[0];
  const set = (changes) => onChange({ ...value, ...changes });
  const setNew = (changes) => set({ newManager: { ...value.newManager, ...changes } });

  const options = users.map((user) => ({
    value: user.id,
    label: `${user.name} — ${user.email}`,
  }));

  return (
    <div className="md:col-span-2 rounded-lg border border-line p-4">
      <h2 className="mb-1 text-sm font-semibold">{t("branchForm.manager.title")}</h2>
      <p className="mb-3 text-xs text-ink-2">{t("branchForm.manager.hint")}</p>

      {/* Only worth saying where "keep them" is one of the answers. */}
      {modes.includes("unchanged") && (
        <p className="mb-3 text-xs text-ink-2">
          {currentManager
            ? t("branchForm.manager.current", {
                name: currentManager.name,
                email: currentManager.email,
              })
            : t("branchForm.manager.currentNone")}
        </p>
      )}

      <div className="mb-4 flex flex-wrap gap-4">
        {modes.map((mode) => (
          <label key={mode} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name="manager_mode"
              className="h-4 w-4 border-line-strong"
              checked={value.managerMode === mode}
              onChange={() => set({ managerMode: mode })}
            />
            {t(`branchForm.manager.${mode}`)}
          </label>
        ))}
      </div>
      {fieldError("manager_mode") && <p className="field-error">{fieldError("manager_mode")}</p>}

      {value.managerMode === "existing" && usersFailed && (
        <p className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {t("branchForm.manager.usersFailed")}
        </p>
      )}

      {value.managerMode === "existing" && !usersFailed && (
        <SearchableSelect
          id="managerUserId"
          label={t("branchForm.manager.pickUser")}
          required
          options={options}
          value={value.userId}
          onChange={(userId) => set({ userId })}
          placeholder={loading ? t("common.loading") : t("branchForm.manager.selectUser")}
          disabled={loading}
          error={fieldError("user_id")}
          emptyText={t("branchForm.manager.noUsers")}
        />
      )}

      {value.managerMode === "new" && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="managerName">
              {t("branchForm.manager.name")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="managerName"
              className="field-input"
              value={value.newManager.name}
              onChange={(e) => setNew({ name: e.target.value })}
            />
            {fieldError("new_manager.name") && (
              <p className="field-error">{fieldError("new_manager.name")}</p>
            )}
          </div>

          <div>
            <label className="field-label" htmlFor="managerEmail">
              {t("branchForm.manager.email")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="managerEmail"
              type="email"
              dir="ltr"
              className="field-input"
              value={value.newManager.email}
              onChange={(e) => setNew({ email: e.target.value })}
            />
            {fieldError("new_manager.email") && (
              <p className="field-error">{fieldError("new_manager.email")}</p>
            )}
          </div>

          <div>
            <label className="field-label" htmlFor="managerPassword">
              {t("branchForm.manager.password")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="managerPassword"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              className="field-input"
              value={value.newManager.password}
              onChange={(e) => setNew({ password: e.target.value })}
            />
            <p className="mt-1 text-xs text-ink-2">{t("branchForm.manager.passwordHint")}</p>
            {fieldError("new_manager.password") && (
              <p className="field-error">{fieldError("new_manager.password")}</p>
            )}
          </div>

          <div>
            <label className="field-label" htmlFor="managerPasswordConfirm">
              {t("branchForm.manager.passwordConfirmation")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="managerPasswordConfirm"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              className="field-input"
              value={value.newManager.passwordConfirmation}
              onChange={(e) => setNew({ passwordConfirmation: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
